import React, { useMemo } from 'react'
import { ButtonType, CustomButton, InfoTooltip } from '../../../../../components'
import { CustomCheckbox } from '../../components/CustomCheckbox'
import { CustomSelect } from '../../components/CustomSelect'
import { ViewBox } from '../../components/ViewBox'
import { Inputs } from '../../components/Inputs'
import { CashlessJobs, Currency } from './terminal.const'
import styles from './TerminalBasicConfigUI.module.css'

const numberFields = [
    'inactivity_timeout_ms',
    'inactivity_countdown_s',
    'inactivity_countdown_successful_page_s',
    'overpaid_countdown_s',
    'treasure_capacity',
    'currency_code',
    'cashless_pu',
    'cashless_tid'
]

const SECTION_KEYS = {
    GENERAL: 'generalSection',
    UI: 'userInterfaceSection',
    CASH: 'cashSection',
    CASHLESS: 'cashlessSection',
    BARCODE: 'barcodeSection',
    PRINTER: 'printerSection'
}

const handleInputChange = (key, value, onUpdate) => {
    const newValue = numberFields.includes(key) ? (value === '' ? '' : Number(value)) : value
    onUpdate(key, newValue)
}

const sectionData = (data) => ({
    general: [
        { name: 'Sibling Lot Default', value: data.lot_id, key: 'lot_id' }
        // { name: 'Terminal ID', value: data.terminal_id, key: 'terminal_id', dataType: 'number' }
    ],
    userInterface: [
        {
            name: 'Time until inactivity question [ms]',
            value: data.inactivity_timeout_ms,
            key: 'inactivity_timeout_ms',
            dataType: 'number'
        },
        {
            name: 'Time to answer inactivity question [s]',
            value: data.inactivity_countdown_s,
            key: 'inactivity_countdown_s',
            dataType: 'number'
        },
        {
            name: 'Time to view success page [s]',
            value: data.inactivity_countdown_successful_page_s,
            key: 'inactivity_countdown_successful_page_s',
            dataType: 'number'
        },
        {
            name: 'Time to react to overpayment question [s]',
            value: data.overpaid_countdown_s,
            key: 'overpaid_countdown_s',
            dataType: 'number'
        }
    ],
    cash: [
        {
            name: 'Treasure Capacity',
            value: data.treasure_capacity,
            key: 'treasure_capacity',
            dataType: 'number'
        }
    ],
    cashless: [
        {
            name: 'PU Number (PU)',
            value: data.cashless_pu,
            key: 'cashless_pu',
            dataType: 'number'
        },
        {
            name: 'Terminal ID (TID)',
            value: data.cashless_tid,
            key: 'cashless_tid',
            dataType: 'number'
        }
    ]
})

interface TerminalBasicConfigUIProps {
    data: any
    onUpdate: any
    onSave: () => void
    onForceUpdate: () => void
    setDefaultToBaseForSection: any
    disabled: boolean
    forceUpdateDisabled: boolean
    validationErrors: any
    avaiableLots: any[]
    loadingSave: boolean
    loadingForceUpdate: boolean
}

const TerminalBasicConfigUI = ({
    data,
    onUpdate,
    onSave,
    onForceUpdate,
    setDefaultToBaseForSection,
    disabled,
    forceUpdateDisabled,
    validationErrors,
    avaiableLots,
    loadingSave,
    loadingForceUpdate
}: TerminalBasicConfigUIProps) => {
    const sections = sectionData(data)
    const currencyOptions = useMemo(() => Object.entries(Currency).map(([key]) => ({ id: key, label: key })), [])

    return (
        <div className={styles.root}>
            <div className={styles.actions}>
                <CustomButton
                    type={ButtonType.SAVE}
                    onClick={onSave}
                    disabled={disabled || loadingSave || loadingForceUpdate}
                    isLoading={loadingSave}
                >
                    Save Configuration
                </CustomButton>
                <InfoTooltip content={forceUpdateDisabled ? 'No changes to force update' : null}>
                    <CustomButton
                        type={ButtonType.DELETE}
                        onClick={onForceUpdate}
                        disabled={forceUpdateDisabled || loadingSave || loadingForceUpdate}
                        isLoading={loadingForceUpdate}
                    >
                        Force Update
                    </CustomButton>
                </InfoTooltip>
            </div>

            <div className={styles.columns}>
                <div className={styles.column}>
                    <ViewBox
                        onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.GENERAL)}
                        title="General"
                        toggleLable="Test mode"
                        checked={data.test_mode}
                        setChecked={() => onUpdate('test_mode', !data.test_mode)}
                        confirmText="Are you sure you want to enable testmode? Technical teams will not be alerted during this time in case of failures!"
                    >
                        <CustomSelect
                            label={sections.general[0].name}
                            options={[
                                { id: undefined, label: 'None' },
                                ...avaiableLots.map((lot) => ({ id: lot, label: lot }))
                            ]}
                            value={{
                                id: data.lot_id,
                                label: data.lot_id
                            }}
                            onChange={(selected) => {
                                if (selected) {
                                    handleInputChange('lot_id', selected.id, onUpdate)
                                }
                            }}
                        />
                    </ViewBox>
                    <ViewBox onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.UI)} title="User Interface">
                        <Inputs
                            data={sections.userInterface}
                            onInputChange={(key, val) => handleInputChange(key, val, onUpdate)}
                            validationErrors={validationErrors}
                        />
                    </ViewBox>
                </div>
                <div className={styles.column}>
                    <ViewBox
                        onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.CASH)}
                        title="Cash"
                        toggleLable="Component Enabled"
                        checked={!data.remove_cash_module}
                        setChecked={() => onUpdate('remove_cash_module', !data.remove_cash_module)}
                    >
                        <Inputs
                            data={sections.cash}
                            onInputChange={(key, val) => handleInputChange(key, val, onUpdate)}
                            validationErrors={validationErrors}
                        />
                        <CustomSelect
                            label="Currency"
                            options={currencyOptions}
                            value={{
                                id: data.currency_code,
                                label:
                                    Object.keys(Currency).find((key) => Currency[key] === data.currency_code) ||
                                    data.currency_code
                            }}
                            onChange={(selected) => selected && onUpdate('currency_code', Currency[selected.id])}
                        />
                    </ViewBox>

                    <ViewBox
                        onDefault={() => setDefaultToBaseForSection(SECTION_KEYS.CASHLESS)}
                        title="Cashless"
                        toggleLable="Component Enabled"
                        checked={!data.remove_cashless_module}
                        setChecked={() => onUpdate('remove_cashless_module', !data.remove_cashless_module)}
                    >
                        <CustomCheckbox
                            checked={data.cashless_configure_enabled}
                            onChange={(e) => onUpdate('cashless_configure_enabled', e.currentTarget.checked)}
                        >
                            Run Job on Start
                        </CustomCheckbox>
                        <CustomSelect
                            label="Job"
                            options={Object.entries(CashlessJobs).map(([id, label]) => ({
                                id,
                                label
                            }))}
                            value={{
                                id: data.cashless_configure_job,
                                label: CashlessJobs[data.cashless_configure_job] || data.cashless_configure_job
                            }}
                            onChange={(job) => job && onUpdate('cashless_configure_job', job.id)}
                            disabled={!data.cashless_configure_enabled}
                        />
                        <div className={styles.cashlessInputs}>
                            <Inputs
                                data={sections.cashless}
                                onInputChange={(key, val) => handleInputChange(key, val, onUpdate)}
                                disabled={
                                    !data.cashless_configure_enabled ||
                                    (data.cashless_configure_job !== 'cashless_config_job' &&
                                        data.cashless_configure_job !== 'initialization_job')
                                }
                                validationErrors={validationErrors}
                            />
                        </div>
                    </ViewBox>
                    {['Barcode', 'Printer'].map((title) => (
                        <ViewBox
                            onDefault={() => setDefaultToBaseForSection(`${title.toLocaleLowerCase()}Section`)}
                            key={title}
                            title={title}
                            toggleLable="Component Enabled"
                            checked={!data[`remove_${title.toLowerCase()}_module`]}
                            setChecked={() =>
                                onUpdate(
                                    `remove_${title.toLowerCase()}_module`,
                                    !data[`remove_${title.toLowerCase()}_module`]
                                )
                            }
                        ></ViewBox>
                    ))}
                </div>
            </div>
        </div>
    )
}

export default TerminalBasicConfigUI
